/**
 * 웹 데모용 볼륨 저장소.
 *
 * 확장의 `volume-store.ts`와 같은 OPFS 계약(`packages/core/src/volumes.ts`)을
 * 쓰지만 **네트워크 경로가 없다**. 매니페스트 fetch도, Range 다운로드도 없다.
 * 오직 사용자가 고른 로컬 `.aknpkg` 파일만 OPFS로 스트리밍한다.
 *
 * - 체크포인트: localStorage (확장은 chrome.storage.local)
 * - 파일: OPFS `beoptong-volumes/<fileName>` (검색 워커가 SAHPool로 adopt)
 */
import {
  VOLUME_SPECS,
  detectVolumeStates,
  initialVolumeState,
  initialVolumeStates,
  reduceVolumeState,
  type VolumeEvent,
  type VolumeKey,
  type VolumeStates,
} from "@beoptong/core";
import { createOpfsVolumeSink } from "../../../packages/ranker/src/volume-sink";
import {
  KeyValueVolumeCheckpointStore,
  VolumeDownloader,
} from "../../../packages/ranker/src/volume-downloader";
import type {
  VolumeCheckpointKV,
  VolumeProgress,
} from "../../../packages/ranker/src/volume-downloader";
import type { VolumeSink } from "../../../packages/ranker/src/volume-core";

export interface LocalVolumeStoreOptions {
  onState?: (states: VolumeStates) => void;
  /** OPFS에 최종 파일이 놓였을 때. 검색 워커 reopen 트리거. */
  onInstalled?: (key: VolumeKey) => void;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** localStorage는 동기지만 인터페이스는 Promise다. 실패는 조용히 무시한다. */
const checkpoints: VolumeCheckpointKV = {
  async get(key) {
    try {
      return localStorage.getItem(`beoptong:demo:volume:${key}`);
    } catch {
      return null;
    }
  },
  async set(key, value) {
    try {
      localStorage.setItem(`beoptong:demo:volume:${key}`, value);
    } catch {
      // 용량 초과 등은 이어받기만 포기하면 된다.
    }
  },
  async remove(key) {
    try {
      localStorage.removeItem(`beoptong:demo:volume:${key}`);
    } catch {
      // ignore
    }
  },
};

export class LocalVolumeStore {
  private readonly options: LocalVolumeStoreOptions;
  private readonly sink: VolumeSink;
  private readonly store = new KeyValueVolumeCheckpointStore(checkpoints);
  private readonly downloaders = new Map<VolumeKey, VolumeDownloader>();
  private readonly notified = new Set<VolumeKey>();
  private state: VolumeStates = initialVolumeStates();
  private disposed = false;

  private constructor(options: LocalVolumeStoreOptions, sink: VolumeSink) {
    this.options = options;
    this.sink = sink;
  }

  static async create(options: LocalVolumeStoreOptions): Promise<LocalVolumeStore> {
    const { sink } = await createOpfsVolumeSink();
    const store = new LocalVolumeStore(options, sink);
    await store.init();
    return store;
  }

  private async init(): Promise<void> {
    this.state = await detectVolumeStates(this.sink);
    for (const spec of VOLUME_SPECS) {
      // artifact를 주지 않으면 importFile(에어갭)만 가능하다.
      const downloader = new VolumeDownloader({
        name: spec.key,
        fileName: spec.fileName,
        store: this.store,
        sink: this.sink,
        onProgress: (progress) => this.onProgress(spec.key, progress),
      });
      this.downloaders.set(spec.key, downloader);
    }
    this.emit();
  }

  private emit(): void {
    if (this.disposed) return;
    this.options.onState?.({ ...this.state });
  }

  private set(key: VolumeKey, event: VolumeEvent): void {
    const current = this.state[key] ?? initialVolumeState(key);
    this.state = { ...this.state, [key]: reduceVolumeState(current, event) };
    this.emit();
  }

  private onProgress(key: VolumeKey, progress: VolumeProgress): void {
    switch (progress.phase) {
      case "idle":
        this.set(key, { type: "reset" });
        return;
      case "downloading":
        this.set(key, {
          type: "progress",
          bytes: progress.bytes,
          total: progress.total,
          rate: progress.rate,
          eta: progress.eta,
          source: progress.source,
        });
        return;
      case "verifying":
        this.set(key, { type: "verify", bytes: progress.bytes, total: progress.total });
        return;
      case "paused":
        this.set(key, { type: "pause", bytes: progress.bytes });
        return;
      case "ready":
        this.set(key, {
          type: "installed",
          bytes: progress.bytes,
          total: progress.total,
        });
        if (!this.notified.has(key)) {
          this.notified.add(key);
          this.options.onInstalled?.(key);
        }
        return;
      case "error":
        this.set(key, {
          type: "error",
          error: progress.error ?? "가져오기에 실패했습니다.",
          bytes: progress.bytes,
        });
        return;
      default: {
        const never: never = progress.phase;
        return never;
      }
    }
  }

  states(): VolumeStates {
    return { ...this.state };
  }

  async importFile(key: VolumeKey, file: File): Promise<void> {
    const downloader = this.downloaders.get(key);
    if (!downloader) throw new Error(`unknown volume: ${key}`);
    this.notified.delete(key);
    this.set(key, { type: "queued", total: file.size });
    try {
      await downloader.importFile(file);
    } catch (error) {
      if (this.state[key]?.phase !== "error") {
        this.set(key, { type: "error", error: errorMessage(error) });
      }
      throw error;
    }
  }

  pause(key: VolumeKey): void {
    this.downloaders.get(key)?.pause();
  }

  async cancel(key: VolumeKey): Promise<void> {
    await this.downloaders.get(key)?.cancel();
    this.notified.delete(key);
    this.set(key, { type: "reset" });
  }

  /** OPFS를 다시 스캔한다(다른 탭/이전 세션에서 가져온 볼륨 반영). */
  async refresh(): Promise<void> {
    const detected = await detectVolumeStates(this.sink);
    const next: VolumeStates = { ...this.state };
    for (const spec of VOLUME_SPECS) {
      const state = detected[spec.key];
      if (state.phase === "installed" && next[spec.key]?.phase !== "installed") {
        next[spec.key] = state;
      }
    }
    this.state = next;
    this.emit();
  }

  async dispose(): Promise<void> {
    if (this.disposed) return;
    this.disposed = true;
    await Promise.allSettled(
      [...this.downloaders.values()].map((downloader) => downloader.dispose()),
    );
    await this.sink.close().catch(() => undefined);
  }
}
